import "./Form.scss";

import createFileList from "./helpers/createFileList";
import filterFileList from "./helpers/filterFileList";
import FileDialog from "./FileDialog";
import data from "./data";
import generateVideoPoster from "./helpers/generateVideoPoster";

/**
 * A class to wrap and extend the functionality of the given form to allow for better file uploading.
 */
export default class Form {
    /**
     * Create a new instance of the class.
     * @param {HTMLElement} form
     */
    constructor( form ) {
        this.onDragOver = this.onDragOver.bind( this );
        this.onDragDrop = this.onDragDrop.bind( this );
        this.onFilesChanged = this.onFilesChanged.bind( this );

        this.element = form;
        this.i18n = Object.assign( {}, data.i18n, JSON.parse( this.element.dataset.i18n ?? "{}" ) );
        this.dropZone = this.element.querySelector( ".fg-feu-drop-zone" );
        this.dropZone?.addEventListener( "dragover", this.onDragOver );
        this.dropZone?.addEventListener( "drop", this.onDragDrop );
        this.fileInput = this.element.querySelector( "input[name='foogallery_images[]']" );
        this.fileInput?.addEventListener( "change", this.onFilesChanged );
        this.fields = ( this.element.dataset.fields?.split( " " ) ?? [] ).filter( f => typeof f === "string" && f.length > 0 );
        this.requiredFields = ( this.element.dataset.requiredFields?.split( " " ) ?? [] ).filter( f => typeof f === "string" && f.length > 0 );
        this.maxSize = parseInt( this.element.dataset.maxSize ?? 0 ) * 1024 * 1024;
        this.maxImages = parseInt( this.element.dataset.maxImages ?? 0 );
        this.allowedTypes = ( this.element.dataset.allowedTypes?.split( "," ) ?? [] )
            .map( type => type.trim().toLowerCase() )
            .filter( Boolean );
        this.allowVideoUploads = ( this.element.dataset.allowVideoUploads ?? ( data.allowVideoUploads ? "yes" : "no" ) ) === "yes";
        this.videoFeatureAvailable = ( this.element.dataset.videoFeatureAvailable ?? ( data.videoFeatureAvailable ? "yes" : "no" ) ) === "yes";
        this.videoUploadsEnabled = ( this.element.dataset.videoUploadsEnabled ?? "no" ) === "yes";
        this.uploadItems = [];
        this.dialog = null;
    }

    get hasAnyFiles() {
        return this.uploadItems.length > 0;
    }

    get hasSingleFile() {
        return this.uploadItems.length === 1;
    }

    get hasFields() {
        return this.fields.length > 0;
    }

    get hasValidUploadItems() {
        return this.uploadItems.some( item => !item.error );
    }

    get files() {
        return this.uploadItems.map( item => item.file );
    }

    async upload( formData ) {
        try {
            const response= await fetch( data.rest.endpoint, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "X-WP-Nonce": data.rest.nonce
                },
                body: formData
            } );
            const contentType = response.headers.get( "content-type" );
            if ( !contentType || !contentType.includes( "application/json" ) ) {
                return { success: false, message: this.i18n.restBadResponse };
            }
            const result = await response.json();
            return !response.ok ? Object.assign( result, { success: false } ) : result;
        } catch ( err ) {
            return { success: false, message: this.i18n.restNetworkError, error: err };
        }
    }

    releaseUploadItem( item ) {
        if ( item && typeof item.previewUrl === "string" && item.previewUrl.startsWith( "blob:" ) ) {
            URL.revokeObjectURL( item.previewUrl );
        }
    }

    clearUploadItems() {
        this.uploadItems.forEach( item => this.releaseUploadItem( item ) );
        this.uploadItems = [];
    }

    setUploadItems( items ) {
        const nextItems = Array.isArray( items ) ? items : [];
        this.uploadItems
            .filter( item => !nextItems.includes( item ) )
            .forEach( item => this.releaseUploadItem( item ) );
        this.uploadItems = nextItems;
        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.files = createFileList( this.uploadItems.map( item => item.file ) );
        }
    }

    removeUploadItem( index ) {
        if ( index < 0 || index >= this.uploadItems.length ) {
            return;
        }

        const [ removed ] = this.uploadItems.splice( index, 1 );
        this.releaseUploadItem( removed );

        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.files = createFileList( this.uploadItems.map( item => item.file ) );
        }
    }

    getFileExtension( file ) {
        return file.name.split( "." ).pop()?.toLowerCase() ?? "";
    }

    getFileKind( file ) {
        const extension = this.getFileExtension( file );
        const imageExtensions = [ "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif" ];

        if ( extension === "mp4" || file.type === "video/mp4" || file.type.startsWith( "video/" ) ) {
            return "video";
        }

        if ( file.type.startsWith( "image/" ) || imageExtensions.includes( extension ) ) {
            return "image";
        }

        return "image";
    }

    isAllowedFileType( file ) {
        if ( !this.allowedTypes.length ) return true;
        const extension = this.getFileExtension( file );
        return extension.length > 0 && this.allowedTypes.includes( extension );
    }

    isSupportedFile( file ) {
        const kind = this.getFileKind( file );
        const extension = this.getFileExtension( file );

        if ( kind === "video" ) {
            return this.videoUploadsEnabled && this.isAllowedFileType( file ) && this.getFileExtension( file ) === "mp4";
        }

        return this.isAllowedFileType( file ) && ( file.type.startsWith( "image/" ) || [ "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif" ].includes( extension ) );
    }

    async createUploadItem( file ) {
        const kind = this.getFileKind( file );

        if ( kind === "video" && this.videoUploadsEnabled ) {
            try {
                const poster = await generateVideoPoster( file );
                return {
                    file,
                    kind,
                    posterFile: poster.posterFile,
                    previewUrl: poster.previewUrl,
                    dimensions: {
                        width: poster.width,
                        height: poster.height
                    },
                    error: ""
                };
            } catch ( error ) {
                return {
                    file,
                    kind,
                    posterFile: null,
                    previewUrl: "",
                    dimensions: null,
                    error: this.i18n.videoPosterFailed
                };
            }
        }

        return {
            file,
            kind,
            posterFile: null,
            previewUrl: URL.createObjectURL( file ),
            dimensions: null,
            error: ""
        };
    }

    async prepareUploadItems( fileList ) {
        this.clearUploadItems();
        const files = Array.from( fileList ?? [] );
        this.uploadItems = await Promise.all( files.map( file => this.createUploadItem( file ) ) );
        return this.uploadItems;
    }

    reset() {
        if ( this.fileInput instanceof HTMLInputElement ) {
            this.fileInput.value = null;
        }
        this.clearUploadItems();
    }

    async onFilesChanged() {
        if ( !this.fileInput || !this.fileInput.files || this.fileInput.files.length === 0 ) {
            return;
        }

        await this.prepareUploadItems( this.fileInput.files );
        if ( this.dialog === null ) this.dialog = new FileDialog( this );
        this.dialog.open();
    }

    onDragOver( event ) {
        if ( event.dataTransfer.items.length > 0 ) {
            event.preventDefault();
        }
    }

    onDragDrop( event ) {
        const dropped = filterFileList( event.dataTransfer.files, file => this.isSupportedFile( file ) );
        if ( event.dataTransfer.files.length > 0 ) {
            event.preventDefault();
        }
        if ( dropped.length > 0 ) {
            this.fileInput.files = dropped;
            this.onFilesChanged();
        }
    }
};

//region type-defs

/**
 * @typedef {object} BadResponse
 * @property {false} success
 * @property {string} message
 */

/**
 * @typedef {object} NetworkErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {Error} error
 */

/**
 * @typedef {object} WPErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {string|int} code
 * @property {object} data
 */

/**
 * @typedef {{ status: ("succeeded" | "errored" | "skipped"), name: string, message: string }} FileResult
 * @property {("succeeded" | "errored" | "skipped")} status
 * @property {string} name
 * @property {string} message
 */

/**
 * @typedef {object} SuccessResponse
 * @property {true} success
 * @property {string} message
 * @property {FileResult[]} files
 * @property {number} succeeded
 * @property {number} errored
 * @property {number} skipped
 */

/**
 * @typedef {SuccessResponse|WPErrorResponse|BadResponse|NetworkErrorResponse} UploadResponse
 */

//endregion
